import React, { useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import {
  Box,
  Container,
  TextField,
  Button,
  Typography,
  InputAdornment,
  IconButton,
  CircularProgress,
  Alert,
  Collapse,
  LinearProgress,
} from '@mui/material';
import {
  Visibility,
  VisibilityOff,
  Lock,
  ArrowBack,
  Key,
  CheckCircleOutline,
} from '@mui/icons-material';
import { useAppDispatch } from '../../hooks/useAppDispatch';
import { showToast } from '../../store/uiSlice';
import axiosClient from '../../api/axiosClient';
import Logo from '../../assets/header-logo.svg';

const getPasswordStrength = (password: string): { score: number; label: string; color: string } => {
  let score = 0;
  if (password.length >= 8) score++;
  if (/[A-Z]/.test(password)) score++;
  if (/[0-9]/.test(password)) score++;
  if (/[^A-Za-z0-9]/.test(password)) score++;

  if (score <= 1) return { score: 25, label: 'Weak', color: '#f44336' };
  if (score === 2) return { score: 50, label: 'Fair', color: '#ff9800' };
  if (score === 3) return { score: 75, label: 'Good', color: '#2196f3' };
  return { score: 100, label: 'Strong', color: '#4caf50' };
};

const ResetPassword: React.FC = () => {
  const [searchParams] = useSearchParams();
  const [token, setToken] = useState(searchParams.get('token') || '');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState('');
  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  const strength = getPasswordStrength(newPassword);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (!token.trim()) {
      setError('Please enter the reset token.');
      return;
    }
    if (newPassword !== confirmPassword) {
      setError('Passwords do not match.');
      return;
    }
    if (newPassword.length < 8) {
      setError('Password must be at least 8 characters.');
      return;
    }

    setLoading(true);
    try {
      await axiosClient.post('/auth/reset-password', { token: token.trim(), newPassword });
      setDone(true);
      dispatch(showToast({ message: 'Password reset successful! Please login.', type: 'success' }));
      setTimeout(() => navigate('/'), 3000);
    } catch (err: any) {
      const msg = err?.response?.data?.message || 'Reset failed. Token may be invalid or expired.';
      setError(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        background: 'linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)',
        position: 'relative',
        '&::before': {
          content: '""',
          position: 'absolute',
          top: 0, left: 0, right: 0, bottom: 0,
          background:
            'radial-gradient(circle at 20% 50%, rgba(97, 97, 255, 0.05) 0%, transparent 50%), radial-gradient(circle at 80% 80%, rgba(0, 115, 234, 0.05) 0%, transparent 50%)',
          pointerEvents: 'none',
        },
      }}
    >
      <Container
        maxWidth="sm"
        sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', py: 8, position: 'relative', zIndex: 1 }}
      >
        <Box
          sx={{
            width: '100%',
            backgroundColor: 'white',
            borderRadius: 4,
            boxShadow: '0 20px 60px rgba(0, 0, 0, 0.1)',
            overflow: 'hidden',
          }}
        >
          {/* Top Gradient Bar */}
          <Box
            sx={{
              background: 'linear-gradient(135deg, #4A90E2 0%, #5AB9EA 100%)',
              py: 5,
              px: 4,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              color: 'white',
            }}
          >
            <Box
              sx={{
                width: 64,
                height: 64,
                borderRadius: '50%',
                backgroundColor: 'rgba(255,255,255,0.2)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                mb: 2,
              }}
            >
              <Key sx={{ fontSize: 36, color: 'white' }} />
            </Box>
            <Typography variant="h5" fontWeight={700} gutterBottom>
              Reset Password
            </Typography>
            <Typography variant="body2" sx={{ opacity: 0.85, textAlign: 'center' }}>
              Enter your reset token and choose a new strong password.
            </Typography>
          </Box>

          {/* Form Body */}
          <Box sx={{ p: 4 }}>
            {/* Logo */}
            <Box sx={{ display: 'flex', justifyContent: 'center', mb: 3 }}>
              <img src={Logo} alt="Agile Edge" style={{ height: 36 }} />
            </Box>

            <Collapse in={!done}>
              <Box component="form" onSubmit={handleSubmit}>
                <Collapse in={!!error}>
                  <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }}>
                    {error}
                  </Alert>
                </Collapse>

                {/* Token field */}
                <TextField
                  fullWidth
                  label="Reset Token"
                  type="text"
                  value={token}
                  onChange={(e) => setToken(e.target.value)}
                  placeholder="Paste your reset token here"
                  required
                  sx={{ mb: 3 }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Key color="action" />
                      </InputAdornment>
                    ),
                    style: { fontFamily: 'monospace', fontSize: '0.85rem' },
                  }}
                />

                {/* New Password */}
                <TextField
                  fullWidth
                  label="New Password"
                  type={showPassword ? 'text' : 'password'}
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  placeholder="Enter new password (min 8 characters)"
                  required
                  sx={{ mb: 1 }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Lock color="action" />
                      </InputAdornment>
                    ),
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton onClick={() => setShowPassword(!showPassword)} edge="end" tabIndex={-1}>
                          {showPassword ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />

                {/* Password Strength Bar */}
                {newPassword && (
                  <Box sx={{ mb: 3 }}>
                    <LinearProgress
                      variant="determinate"
                      value={strength.score}
                      sx={{
                        height: 6,
                        borderRadius: 3,
                        backgroundColor: '#e0e0e0',
                        '& .MuiLinearProgress-bar': { backgroundColor: strength.color, borderRadius: 3 },
                      }}
                    />
                    <Typography variant="caption" sx={{ color: strength.color, fontWeight: 600 }}>
                      {strength.label} password
                    </Typography>
                  </Box>
                )}

                {/* Confirm Password */}
                <TextField
                  fullWidth
                  label="Confirm Password"
                  type={showConfirm ? 'text' : 'password'}
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  placeholder="Re-enter your new password"
                  required
                  sx={{ mb: 3 }}
                  error={!!confirmPassword && newPassword !== confirmPassword}
                  helperText={
                    confirmPassword && newPassword !== confirmPassword ? 'Passwords do not match' : ''
                  }
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Lock color="action" />
                      </InputAdornment>
                    ),
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton onClick={() => setShowConfirm(!showConfirm)} edge="end" tabIndex={-1}>
                          {showConfirm ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />

                <Button
                  type="submit"
                  fullWidth
                  variant="contained"
                  size="large"
                  disabled={loading}
                  sx={{
                    py: 1.5,
                    mb: 2,
                    textTransform: 'none',
                    fontSize: '1rem',
                    fontWeight: 600,
                    background: 'linear-gradient(135deg, #4A90E2 0%, #5AB9EA 100%)',
                    '&:hover': {
                      background: 'linear-gradient(135deg, #357ABD 0%, #4AA3D5 100%)',
                    },
                  }}
                >
                  {loading ? <CircularProgress size={24} color="inherit" /> : 'Reset Password'}
                </Button>
              </Box>
            </Collapse>

            {/* Success State */}
            <Collapse in={done}>
              <Box sx={{ textAlign: 'center', py: 3 }}>
                <CheckCircleOutline sx={{ fontSize: 64, color: '#4caf50', mb: 2 }} />
                <Typography variant="h6" fontWeight={700} gutterBottom>
                  Password Reset Successful!
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                  Your password has been updated. Redirecting you to login...
                </Typography>
                <LinearProgress sx={{ borderRadius: 2, mb: 2 }} />
                <Button
                  component={Link}
                  to="/"
                  variant="contained"
                  sx={{
                    textTransform: 'none',
                    background: 'linear-gradient(135deg, #4A90E2 0%, #5AB9EA 100%)',
                  }}
                >
                  Go to Login Now
                </Button>
              </Box>
            </Collapse>

            {/* Back to Login */}
            {!done && (
              <Box sx={{ textAlign: 'center', mt: 1 }}>
                <Button
                  component={Link}
                  to="/forgot-password"
                  startIcon={<ArrowBack />}
                  sx={{ textTransform: 'none', color: 'text.secondary', fontWeight: 500 }}
                >
                  Back to Forgot Password
                </Button>
              </Box>
            )}
          </Box>
        </Box>
      </Container>
    </Box>
  );
};

export default ResetPassword;
